import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { HTMLProps } from 'react';
import { classes } from 'shared';

export function GeneralTextArea({
  name,
  value = '',
  error,
  className,
  iClassName,
  lClassName,
  clean,
  placeholder,
  noHideWithPlaceholder,
  onUpdate,
  onUpdateError,
  iType,
  prefix,
  children,
  ...props
}: {
  name: string;
  value: string | number | undefined;
  error?: string | string[] | undefined;
  clean?: boolean;
  placeholder?: string;
  noHideWithPlaceholder?: boolean;
  /** This modifies the container's classes */
  className?: string;
  iClassName?: string;
  lClassName?: string;
  iType?: 'integrated' | 'classic';
  prefix?: string;
  children?: React.ReactNode;
  onUpdate: (v: string) => void;
  onUpdateError?: (v: string | undefined) => void;
} & HTMLProps<HTMLTextAreaElement>) {
  switch (iType) {
    case 'integrated':
      return renderIntegrated();
    case 'classic':
      return renderClassic();
    default:
      return renderIntegrated();
  }

  function renderClassic() {
    return (
      <div className={classes(className, 'relative')}>
        <label
          htmlFor={sanitize(name)}
          className={classes('inline-flex select-none text-sm font-medium text-gray-700', lClassName)}>
          {name}
          {children}
        </label>
        <div className="mt-1">
          <textarea
            {...props}
            className={calculateClassicClassName()}
            name={sanitize(name)}
            id={sanitize(name)}
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            placeholder={placeholder}
            aria-describedby={`${sanitize(name)}-error`}
          />
        </div>
        <span id={`${sanitize(name)}-error`} className="text-sm text-red-600">
          {getError()}
        </span>
      </div>
    );
  }

  function renderIntegrated() {
    return (
      <div className={classes(className, 'relative mt-[8px]')}>
        <label
          htmlFor={sanitize(name)}
          className={classes(
            'absolute -top-2 left-2 z-[1] -mt-px inline-flex select-none bg-white px-1 text-xs font-medium text-gray-900',
            lClassName,
            (className?.includes('md:pl-2') || className?.includes('md:px-2')) && 'md:ml-2',
            props.disabled && 'bg-gradient-to-b from-white via-white to-gray-200',
            placeholder && !noHideWithPlaceholder && 'hidden',
          )}>
          {name}
          {children}
        </label>
        <div className="relative">
          {prefix && (
            <div className="pointer-events-none absolute inset-y-0 left-0 z-[1] flex items-center pl-3">
              <span className="text-gray-500">{prefix}</span>
            </div>
          )}
          <textarea
            {...props}
            className={calculateIntegratedClassName()}
            name={sanitize(name)}
            id={sanitize(name)}
            value={value || ''}
            onChange={e => handleChange(e.target.value)}
            placeholder={placeholder}
            aria-describedby={`${sanitize(name)}-error`}
          />
          {!!getError() && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          )}
        </div>
        <span id={`${sanitize(name)}-error`} className="text-sm text-red-600">
          {getError()}
        </span>
      </div>
    );
  }

  function getError() {
    if (!error) return;
    if (typeof error !== 'string') return error?.[0];
    return error;
  }

  function calculateClassicClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      'block w-full cursor-text appearance-none rounded-md px-3 py-2 placeholder-gray-400 shadow-sm disabled:bg-gray-200 disabled:text-gray-600 sm:text-sm',
    );
  }

  function calculateIntegratedClassName() {
    if (clean) return iClassName;
    return classes(
      iClassName,
      getError()
        ? 'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
      prefix && 'pl-6',
      'relative block w-full cursor-text rounded-md border bg-white px-4 py-3 pl-3 text-left placeholder-gray-500 shadow-sm focus:outline-none focus:ring-1 disabled:cursor-default disabled:bg-gray-200 disabled:text-gray-600',
    );
  }

  function handleChange(v: string) {
    onUpdate(v);
    if (!!getError() && onUpdateError) onUpdateError(undefined);
  }

  function sanitize(name: string) {
    return name.replace(/[^a-zA-Z0-9]/g, '-');
  }
}
